import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNewbuildingsSummary, getNewbuildingsSummarySafe } from '../../actions/newbuildingsSummary';
import { AiSummaryModalDesktop } from '../../components/AiSummaryModalDesktop';
import type { IAiSummaryFooterActions } from '../../components/AiSummaryModalDesktop/types';
import { selectNewbuildingsSummary } from '../../selectors/newbuildingsSummary';
import type { TThunkDispatch } from '../../types/redux/thunk';
import { NewbuildingCallButton } from '../NewbuildingCallButton';
import { trackShowPhoneAiSummary } from '../NewbuildingCallButton/tracking';
import { NewbuildingChatButton } from '../NewbuildingChatButton';
import {
  trackCardClickAiSummary,
  trackOpenAiModal,
  trackReportErrorAiSummary,
  trackSwitchAiSummaryTab,
} from '../../utils/AiSummaryAnalytics';
import { sendUXSEvent } from '../../utils/uxs';
import { UXFB_EVENT_AI_COMPARISON_REPORT_ERROR_DESKTOP } from '../../constants/uxfb';

export interface IAiSummaryModalDesktopContainerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AiSummaryModalDesktopContainer = ({
  open,
  onOpenChange,
}: IAiSummaryModalDesktopContainerProps): React.JSX.Element => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { data: summary, requestId, status } = useSelector(selectNewbuildingsSummary);
  const [buttonSlot, setButtonSlot] = React.useState<HTMLDivElement | null>(null);
  const [activeCallNewbuildingId, setActiveCallNewbuildingId] = React.useState<number | null>(null);
  const newbuildingIds = React.useMemo(() => summary?.summarization.map(({ id }) => id) ?? [], [summary]);
  const hasTrackedModalOpenRef = React.useRef(false);

  const handleCallButtonSlotChange = React.useCallback((newbuildingId: number, slot: HTMLDivElement | null) => {
    setButtonSlot(slot);

    if (slot) {
      setActiveCallNewbuildingId(newbuildingId);
    }
  }, []);

  const activeNewbuildingContacts = summary?.summarization.find(
    newbuilding => newbuilding.id === activeCallNewbuildingId,
  );

  const handleOpenContacts = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  const handleCloseContacts = React.useCallback(() => onOpenChange(true), [onOpenChange]);
  const handleShowPhone = React.useCallback(() => {
    if (activeNewbuildingContacts) {
      trackShowPhoneAiSummary({
        category: 'Phones',
        newbuildingId: activeNewbuildingContacts.id,
        siteType: 'desktop',
        url: activeNewbuildingContacts.fullUrl,
      });
    }
  }, [activeNewbuildingContacts]);

  React.useEffect(() => {
    if (open) {
      dispatch(getNewbuildingsSummarySafe());
    }
  }, [dispatch, open]);

  React.useEffect(() => {
    if (!open) {
      hasTrackedModalOpenRef.current = false;

      return;
    }

    if (newbuildingIds.length > 0 && open && !hasTrackedModalOpenRef.current) {
      trackOpenAiModal({ JkIds: newbuildingIds, siteType: 'desktop' });
      hasTrackedModalOpenRef.current = true;
    }
  }, [newbuildingIds, open]);

  const handleReload = React.useCallback(() => dispatch(getNewbuildingsSummary()), [dispatch]);
  const handleTabChange = React.useCallback(
    (newbuildingId: number) => trackSwitchAiSummaryTab({ JkId: newbuildingId, siteType: 'desktop' }),
    [],
  );
  const handleReportError = React.useCallback(
    (newbuildingId: number) => {
      trackReportErrorAiSummary({ JkId: newbuildingId, siteType: 'desktop' });
      sendUXSEvent(UXFB_EVENT_AI_COMPARISON_REPORT_ERROR_DESKTOP);
      onOpenChange(false);
    },
    [onOpenChange],
  );
  const handleCardClick = React.useCallback(
    (newbuildingId: number) => trackCardClickAiSummary({ JkId: newbuildingId, siteType: 'desktop' }),
    [],
  );

  const footerActions: IAiSummaryFooterActions[] = (summary?.summarization ?? []).map(newbuilding => {
    const hasChat = [...newbuilding.builders, ...newbuilding.sellers].some(contact => contact.chat?.enabled);

    return {
      newbuildingId: newbuilding.id,
      chat: hasChat ? (
        <NewbuildingChatButton
          builders={newbuilding.builders}
          newbuildingId={newbuilding.id}
          sellers={newbuilding.sellers}
          size="M"
          trackingMode="aiSummary"
          url={newbuilding.fullUrl}
        />
      ) : undefined,
    };
  });

  return (
    <>
      <AiSummaryModalDesktop
        footerActions={footerActions}
        onCallButtonSlotChange={handleCallButtonSlotChange}
        onCardClick={handleCardClick}
        onOpenChange={onOpenChange}
        onReload={handleReload}
        onReportError={handleReportError}
        onTabChange={handleTabChange}
        open={open}
        requestId={requestId}
        status={status}
        summary={summary}
      />
      {activeNewbuildingContacts && (
        <NewbuildingCallButton
          key={activeNewbuildingContacts.id}
          builders={activeNewbuildingContacts.builders}
          buttonSlot={buttonSlot}
          newbuildingId={activeNewbuildingContacts.id}
          onCloseContacts={handleCloseContacts}
          onOpenContacts={handleOpenContacts}
          onShowPhone={handleShowPhone}
          sellers={activeNewbuildingContacts.sellers}
          size="M"
          url={activeNewbuildingContacts.fullUrl}
        />
      )}
    </>
  );
};
